import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import { LandingPage } from './components/LandingPage';
import { RequireAuth } from './components/RequireAuth';
import { AppLayout } from './components/layout/AppLayout';
import { AgentList } from './components/agents/AgentList';
import { AgentKnowledgeBase } from './components/kb/AgentKnowledgeBase';
import { AgentIntegrations } from './components/integrations/AgentIntegrations';
import CRMIntegrations from './components/integrations/CRMIntegrations';
import { PhoneNumbers } from './components/phones/PhoneNumbers';
import { AgentEdit } from './components/agents/AgentEdit';
import { CallLogs } from './components/call-logs/CallLogs';
import { Settings } from './components/settings/Settings';
import { Campaigns } from './components/campaigns/Campaigns';
import { Billing } from './components/billing/Billing';
import { ResearchStudio } from './components/research/ResearchStudio';
import { MarketingPlanner } from './components/planner/MarketingPlanner';
import { WebhookSettings } from './components/webhooks/WebhookSettings';
import { ComplianceCenter } from './components/compliance/ComplianceCenter';
import { CallMonitor } from './components/monitor/CallMonitor';
import { AnalyticsDashboard } from './components/analytics/AnalyticsDashboard';
import { ABTestDashboard } from './components/experiments/ABTestDashboard';
import { analytics } from './services/analytics';

// Initialize PostHog once at module load
analytics.init();

const ProtectedPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireAuth>
    <AppLayout>{children}</AppLayout>
  </RequireAuth>
);

// Track page views on route change
function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    analytics.page(location.pathname, { search: location.search });
  }, [location.pathname, location.search]);
  return null;
}

const App: React.FC = () => {
  return (
    <>
      <PageTracker />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<ProtectedPage><Dashboard /></ProtectedPage>} />
        <Route path="/agents" element={<ProtectedPage><AgentList /></ProtectedPage>} />
        <Route path="/agents/:agentId/edit" element={<ProtectedPage><AgentEdit /></ProtectedPage>} />
        <Route path="/agents/:agentId/kb" element={<ProtectedPage><AgentKnowledgeBase /></ProtectedPage>} />
        <Route path="/agents/:agentId/integrations" element={<ProtectedPage><AgentIntegrations /></ProtectedPage>} />
        <Route path="/phones" element={<ProtectedPage><PhoneNumbers /></ProtectedPage>} />
        <Route path="/call-logs" element={<ProtectedPage><CallLogs /></ProtectedPage>} />
        <Route path="/planner" element={<ProtectedPage><MarketingPlanner /></ProtectedPage>} />
        <Route path="/campaigns" element={<ProtectedPage><Campaigns /></ProtectedPage>} />
        <Route path="/research" element={<ProtectedPage><ResearchStudio /></ProtectedPage>} />
        <Route path="/billing" element={<ProtectedPage><Billing /></ProtectedPage>} />
        <Route path="/analytics" element={<ProtectedPage><AnalyticsDashboard /></ProtectedPage>} />
        <Route path="/compliance" element={<ProtectedPage><ComplianceCenter /></ProtectedPage>} />
        <Route path="/settings" element={<ProtectedPage><Settings /></ProtectedPage>} />
        <Route path="/settings/webhooks" element={<ProtectedPage><WebhookSettings /></ProtectedPage>} />
        <Route path="/integrations" element={<ProtectedPage><CRMIntegrations /></ProtectedPage>} />
        <Route path="/monitor" element={<ProtectedPage><CallMonitor /></ProtectedPage>} />
        <Route path="/experiments" element={<ProtectedPage><ABTestDashboard /></ProtectedPage>} />
      </Routes>
    </>
  );
};

export default App;
